/**
 * `annotationPlacement.ts` — where a comment Spark adds goes, so the user can
 * see it from where they are looking (web-only; Unity floats the bubble a fixed
 * 0.4 m above the part's pivot, wherever that lands).
 *
 * Two searches, both against the live camera, in the manner of Spark's own
 * standing placement (`runtime/assets/sparkBehavior.ts`):
 *
 *   1. ANCHOR — where the user is pointing, when the caller passes a point
 *      on the part they point at (Unity anchors at the pointer hit too).
 *      Otherwise rays across the part's on-screen footprint, nearest its
 *      projected centre first. The first ray whose nearest surface belongs to
 *      the part gives a point on it the user can actually see. A part hidden
 *      from this view falls back to its bounds centre.
 *   2. BUBBLE — candidate spots around the anchor on screen (above it first),
 *      each at the anchor's depth or nearer the camera. A spot is taken only if
 *      the bubble's whole footprint, measured from its real text, stays inside
 *      the part of the viewport no panel covers, sits above the floor, and is
 *      in clear line of sight at every sample. Nearer the camera is fine: the
 *      rule is never BEHIND geometry, and the bubble keeps a constant screen
 *      size, so depth changes only what can hide it.
 *
 * Candidates are costed before any ray is cast and tested cheapest first, so a
 * clear view costs a handful of picks; the tool runs once per call, not per
 * frame. Occlusion uses the same test as Spark: a pick toward the point that
 * hits depth-writing scene geometry first means hidden. Existing comment
 * bubbles never count, and the floor and environment are not scene entities.
 *
 * Positions come back in stage-local Unity space, which is what
 * `IAnnotationsModel.createAnnotationAt` stores and `annotationsRenderer.ts`
 * reads.
 */

import {
  Box3,
  Quaternion,
  Raycaster,
  Vector2,
  Vector3,
  type Camera,
  type Object3D,
  type Ray,
} from "three";
import { Ioc } from "@core/ioc/ioc";
import type { Vec3 } from "@models/data/entityData";
import { ZERO_VIEWPORT_INSETS, type ViewportInsets } from "@models/ui/viewportInsets";
import { ENGINE_SERVICE, type EngineService } from "@runtime/engine/engineService";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import { isDepthOccludingSceneNode } from "@runtime/picking/depthOccluders";
import { ORBIT_CAMERA, type OrbitCameraController } from "@runtime/camera/orbitCameraController";
import { threeWorldPositionToUnity } from "@runtime/assets/participantTransformConversion";
import { worldToStagePosition } from "@runtime/xr/stageSpace";
import { annotationBubbleScale } from "./annotationsRenderer";
import { annotationBubbleSizePx } from "./annotationBubbleCanvas";

export interface AnnotationPlacementRequest {
  /** The part's snapshot id. */
  readonly partId: string;
  /** The part and every snapshot under it: a hit on any of them is the part. */
  readonly partIds: ReadonlySet<string>;
  /** Snapshot ids that never hide anything — the scene's existing comments. */
  readonly ignoredIds: ReadonlySet<string>;
  /** The comment text, which sizes the bubble. */
  readonly text: string;
  /**
   * A point on the part where the user is pointing, in world space. It is the
   * anchor when given, and only the bubble is searched for.
   */
  readonly pointedAnchor?: Readonly<{ x: number; y: number; z: number }> | null;
}

export type AnnotationPlacementResult =
  | {
      readonly kind: "ok";
      readonly anchor: Vec3;
      readonly placement: Vec3;
      /** False when no point of the part is visible from this view. */
      readonly anchorVisible: boolean;
      /** False when no candidate was fully clear and the least-hidden one was used. */
      readonly bubbleClear: boolean;
    }
  | { readonly kind: "viewport-unavailable" }
  | { readonly kind: "no-bounds" };

/** Gap kept between the bubble and the viewport edge or a panel, in CSS px. */
const K_FRAME_MARGIN_PX = 16;
/** Safe frame, in NDC, when the canvas has no measurable size. */
const K_FALLBACK_SAFE_NDC = 0.94;
/** Anchor rays per side across the part's on-screen footprint. */
const K_ANCHOR_GRID = 7;
/** A hit this much short of a sample point (m) still counts as reaching it. */
const K_OCCLUSION_EPSILON_M = 0.005;
/** Bubble-bottom offsets from the anchor, in NDC height units, nearest first. */
const K_RING_RADII = [0.14, 0.24, 0.36] as const;
/** Screen directions with the cost of choosing them: above reads best. */
const K_DIRECTIONS: ReadonlyArray<readonly [number, number, number]> = [
  [0, 1, 0],
  [Math.SQRT1_2, Math.SQRT1_2, 0.02],
  [-Math.SQRT1_2, Math.SQRT1_2, 0.02],
  [1, 0, 0.05],
  [-1, 0, 0.05],
  [Math.SQRT1_2, -Math.SQRT1_2, 0.12],
  [-Math.SQRT1_2, -Math.SQRT1_2, 0.12],
  [0, -1, 0.15],
];
/** Fractions of the anchor's distance tried for the bubble, farthest first. */
const K_DEPTH_FRACTIONS = [1, 0.8, 0.6, 0.4, 0.25] as const;
/** Cost per unit of depth pulled toward the camera. */
const K_DEPTH_COST = 0.6;
/** Bubble samples across its card, as (right, up) fractions of its size. */
const K_BUBBLE_SAMPLES: ReadonlyArray<readonly [number, number]> = [
  [0, 0.5], [-0.45, 0.1], [0.45, 0.1], [-0.45, 0.9], [0.45, 0.9],
  [0, 0.1], [0, 0.9], [-0.45, 0.5], [0.45, 0.5],
];

interface NdcRect {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface BubbleCandidate {
  readonly ndc: Vector2;
  readonly depth: number;
  readonly cost: number;
}

export function findAnnotationPlacement(request: AnnotationPlacementRequest): AnnotationPlacementResult {
  const engine = Ioc.isRegistered<EngineService>(ENGINE_SERVICE)
    ? Ioc.resolve<EngineService>(ENGINE_SERVICE)
    : null;
  const stageRoot = engine?.rootOrNull ?? null;
  const camera = stageRoot ? engine!.app.camera : null;
  if (!engine || !stageRoot || !camera || !Ioc.isRegistered<PickingService>(PICKING_SERVICE)) {
    return { kind: "viewport-unavailable" };
  }
  const picker = Ioc.resolve<PickingService>(PICKING_SERVICE);
  const node = picker.resolveSnapshotNode(request.partId);
  if (!node) return { kind: "no-bounds" };
  const bounds = new Box3().setFromObject(node);
  if (bounds.isEmpty()) return { kind: "no-bounds" };

  camera.updateMatrixWorld();
  const cameraPosition = camera.getWorldPosition(new Vector3());
  const insets = Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)
    ? Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).viewportInsets
    : ZERO_VIEWPORT_INSETS;
  const canvas = engine.app.canvas;
  const frame = safeFrame(canvas?.clientWidth ?? 0, canvas?.clientHeight ?? 0, insets);
  const aspect = canvas && canvas.clientHeight > 0 ? canvas.clientWidth / canvas.clientHeight : camera.aspect;

  const raycaster = new Raycaster();
  const isOccluder = (n: Object3D): boolean => {
    const id = picker.resolveSnapshotId(n);
    return id !== null && !request.ignoredIds.has(id) && isDepthOccludingSceneNode(picker, n);
  };
  const rayThrough = (ndc: Vector2): Ray => {
    raycaster.setFromCamera(ndc, camera);
    return raycaster.ray;
  };
  const isHidden = (point: Vector3): boolean => {
    const direction = point.clone().sub(cameraPosition);
    const distance = direction.length();
    if (distance <= K_OCCLUSION_EPSILON_M) return false;
    raycaster.ray.set(cameraPosition, direction.multiplyScalar(1 / distance));
    const hit = picker.pickFromRay(raycaster.ray, isOccluder);
    return hit !== null && hit.distance < distance - K_OCCLUSION_EPSILON_M;
  };

  // 1. Anchor: where the user points, else a point on the part this view can see.
  const pointed = request.pointedAnchor ?? null;
  let anchor: Vector3 | null = pointed ? new Vector3(pointed.x, pointed.y, pointed.z) : null;
  const footprint = anchor ? null : projectedRect(bounds, camera, frame);
  if (footprint) {
    const centre = bounds.getCenter(new Vector3()).project(camera);
    const cx = clamp(centre.x, footprint.minX, footprint.maxX);
    const cy = clamp(centre.y, footprint.minY, footprint.maxY);
    const points: Vector2[] = [];
    for (let i = 0; i < K_ANCHOR_GRID; i++) {
      for (let j = 0; j < K_ANCHOR_GRID; j++) {
        points.push(new Vector2(
          lerp(footprint.minX, footprint.maxX, (i + 0.5) / K_ANCHOR_GRID),
          lerp(footprint.minY, footprint.maxY, (j + 0.5) / K_ANCHOR_GRID),
        ));
      }
    }
    points.push(new Vector2(cx, cy));
    points.sort((a, b) => Math.hypot(a.x - cx, a.y - cy) - Math.hypot(b.x - cx, b.y - cy));
    for (const ndc of points) {
      const hit = picker.pickFromRay(rayThrough(ndc), isOccluder);
      if (hit?.snapshotId && request.partIds.has(hit.snapshotId)) {
        anchor = new Vector3(hit.worldPoint.x, hit.worldPoint.y, hit.worldPoint.z);
        break;
      }
    }
  }
  // A pointed point was in sight when the user pointed; the view may have moved since.
  const anchorVisible = anchor !== null && (!pointed || isInSight(anchor, camera, frame, isHidden));
  const anchorWorld = anchor ?? bounds.getCenter(new Vector3());

  // 2. Bubble: the cheapest spot around the anchor whose card is fully in sight.
  const anchorNdc = anchorWorld.clone().project(camera);
  const anchorDistance = cameraPosition.distanceTo(anchorWorld);
  const { widthPx, heightPx } = annotationBubbleSizePx(request.text);
  // The bubble billboards to the camera, so its card spans camera right × up.
  const cameraQuaternion = camera.getWorldQuaternion(new Quaternion());
  const right = new Vector3(1, 0, 0).applyQuaternion(cameraQuaternion);
  const up = new Vector3(0, 1, 0).applyQuaternion(cameraQuaternion);
  const minDepth = camera.near * 4;

  const candidates: BubbleCandidate[] = [];
  for (const radius of K_RING_RADII) {
    for (const [dx, dy, directionCost] of K_DIRECTIONS) {
      for (const fraction of K_DEPTH_FRACTIONS) {
        candidates.push({
          ndc: new Vector2(anchorNdc.x + (dx * radius) / aspect, anchorNdc.y + dy * radius),
          depth: Math.max(minDepth, anchorDistance * fraction),
          cost: radius + directionCost + (1 - fraction) * K_DEPTH_COST,
        });
      }
    }
  }
  candidates.sort((a, b) => a.cost - b.cost);

  let best: { point: Vector3; clear: number } | null = null;
  const corner = new Vector3();
  const sample = new Vector3();
  for (const candidate of candidates) {
    const point = rayThrough(candidate.ndc).at(candidate.depth, new Vector3());
    if (worldToStagePosition(stageRoot, point, new Vector3()).y < 0) continue;
    const scale = annotationBubbleScale(candidate.depth);
    const halfWidth = widthPx * scale * 0.5;
    const height = heightPx * scale;
    let inFrame = true;
    for (const [sx, sy] of [[-1, 0], [1, 0], [-1, 1], [1, 1]] as const) {
      corner.copy(point).addScaledVector(right, sx * halfWidth).addScaledVector(up, sy * height);
      corner.project(camera);
      if (
        corner.z <= -1 || corner.z >= 1
        || corner.x < frame.minX || corner.x > frame.maxX
        || corner.y < frame.minY || corner.y > frame.maxY
      ) {
        inFrame = false;
        break;
      }
    }
    if (!inFrame) continue;

    let clear = 0;
    let hidden = 0;
    const toBeat = best ? K_BUBBLE_SAMPLES.length - best.clear : K_BUBBLE_SAMPLES.length;
    for (const [fx, fy] of K_BUBBLE_SAMPLES) {
      sample.copy(point)
        .addScaledVector(right, fx * 2 * halfWidth)
        .addScaledVector(up, fy * height);
      if (isHidden(sample)) {
        hidden += 1;
        if (hidden >= toBeat) break;
      } else {
        clear += 1;
      }
    }
    if (hidden === 0) {
      best = { point, clear };
      break;
    }
    if (!best || clear > best.clear) best = { point, clear };
  }

  const placementWorld = best?.point
    ?? anchorWorld.clone().addScaledVector(up, anchorDistance * 0.1);
  const bubbleClear = best !== null && best.clear === K_BUBBLE_SAMPLES.length;
  const toUnity = (v: Vector3): Vec3 =>
    threeWorldPositionToUnity(worldToStagePosition(stageRoot, v, new Vector3()));
  return {
    kind: "ok",
    anchor: toUnity(anchorWorld),
    placement: toUnity(placementWorld),
    anchorVisible,
    bubbleClear,
  };
}

/** Is a world point inside the uncovered frame and not behind geometry? */
function isInSight(
  point: Vector3,
  camera: Camera,
  frame: NdcRect,
  isHidden: (point: Vector3) => boolean,
): boolean {
  const ndc = point.clone().project(camera);
  const inFrame = ndc.z < 1
    && ndc.x >= frame.minX && ndc.x <= frame.maxX
    && ndc.y >= frame.minY && ndc.y <= frame.maxY;
  return inFrame && !isHidden(point);
}

/** The viewport no panel covers, shrunk by the margin, in NDC. */
function safeFrame(width: number, height: number, insets: ViewportInsets): NdcRect {
  if (width <= 0 || height <= 0) {
    return {
      minX: -K_FALLBACK_SAFE_NDC,
      maxX: K_FALLBACK_SAFE_NDC,
      minY: -K_FALLBACK_SAFE_NDC,
      maxY: K_FALLBACK_SAFE_NDC,
    };
  }
  return {
    minX: -1 + (2 * (insets.left + K_FRAME_MARGIN_PX)) / width,
    maxX: 1 - (2 * (insets.right + K_FRAME_MARGIN_PX)) / width,
    minY: -1 + (2 * (insets.bottom + K_FRAME_MARGIN_PX)) / height,
    maxY: 1 - (2 * (insets.top + K_FRAME_MARGIN_PX)) / height,
  };
}

/** The bounds' on-screen rectangle clipped to the frame, or null when none of it is in view. */
function projectedRect(
  bounds: Box3,
  camera: Camera,
  frame: NdcRect,
): NdcRect | null {
  const rect: NdcRect = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  const corner = new Vector3();
  let inFront = 0;
  for (let i = 0; i < 8; i++) {
    corner.set(
      i & 1 ? bounds.max.x : bounds.min.x,
      i & 2 ? bounds.max.y : bounds.min.y,
      i & 4 ? bounds.max.z : bounds.min.z,
    ).project(camera);
    if (corner.z >= 1) continue;
    inFront += 1;
    rect.minX = Math.min(rect.minX, corner.x);
    rect.maxX = Math.max(rect.maxX, corner.x);
    rect.minY = Math.min(rect.minY, corner.y);
    rect.maxY = Math.max(rect.maxY, corner.y);
  }
  if (inFront === 0) return null;
  rect.minX = Math.max(rect.minX, frame.minX);
  rect.maxX = Math.min(rect.maxX, frame.maxX);
  rect.minY = Math.max(rect.minY, frame.minY);
  rect.maxY = Math.min(rect.maxY, frame.maxY);
  return rect.minX < rect.maxX && rect.minY < rect.maxY ? rect : null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
